import { combineReducers, configureStore } from '@reduxjs/toolkit'
import commonReducer, { commonInitialState, CommonStateType } from './reducer/common.reducer'
import chartReducer from './reducer/chart.reducer'
import userReducer from './reducer/user.reducer'
import screenerReducer from './reducer/screener.reducer'
import watchListReducer from './reducer/watchList.reducer'
import immutableStateInvariantMiddleware from 'redux-immutable-state-invariant'
import { createTransform, PersistedState, persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import overviewReducer from './reducer/overview.reducer'
import alertLogsReducer from './reducer/alertLogs.reducer'
import alertsReducer from './reducer/alerts.reducer'
import { Redux_Store_Version } from './assets/constants'
import websocketReducer from './reducer/websocket.reducer'
import ordersReducer from './reducer/orders.reducer'
import securityReducer from './reducer/security.reducer'
import orderBookReducer from './reducer/orderBook.reducer'
import positionsReducer from './reducer/positions.reducer'
import { setupListeners } from '@reduxjs/toolkit/query'
import thunk from 'redux-thunk'
import optionsReducer from './reducer/options.reducer'
import widgetDashboardReducer from './reducer/widgetDashboard.reducer'
import futuresReducer from './reducer/futures.reducer'
import homeReducer from './reducer/home.reducer'
import multiStrikeChartReducer, { homeInitialState } from './reducer/multiStrikeChart.reducer'
import strategyBuilderReducer from './reducer/strategyBuilder.reducer'
import tourReducer from './reducer/tour.reducer'
import strategyTemplateReducer from './reducer/strategyTemplates.reducer'
import rehydrationReducer from './reducer/rehydration.reducer'
import globalNotification, { GlobalNotificationsState, golbalNotificationInitialState } from './reducer/globalNotification.reducer'
import companyEventsReducer from "./reducer/companyEvents.reducer";
import simulatorReducer from './reducer/simulator.reducer'
import { getLastWeekday } from './util/tradingCalendar'
import recentSearchReducer from './reducer/recentSearch.reducer'
import screenerChatReducer from './reducer/screenerChat.reducer';
import deviceReducer from './reducer/device.reducer'
import quickTradeReducer from './reducer/quickTrades.reducer'
import holdingsReducer from './reducer/holdings.reducer';
import scalperReducer from './reducer/scalper.reducer'
import feedbackReducer from './reducer/feedback.reducer'
import heatmapReducer from './reducer/heatmap.reducer'
import {logger } from 'redux-logger';
import { isEmbeddedModeSet } from './util/general'
import { quickTradePreferencesMiddleware } from './middleware/quickTradePreferencesMiddleware'
import { quickTradeVisibilityMiddleware } from './middleware/quickTradeVisibilityMiddleware'


const isEmbedded = isEmbeddedModeSet();

const middleware: any[] = [
    quickTradePreferencesMiddleware,
    quickTradeVisibilityMiddleware,
    thunk
]

const enableDebug = import.meta.env.DEV && !['production', 'staging'].includes(import.meta.env.MODE || '');
if (enableDebug) {
    middleware.unshift(immutableStateInvariantMiddleware({
        ignore: ['chartStore.panesData', 'ivPlotStore.plotData', 'websocketStore.subscriptionList', 'ordersStore', 'optionsStore', 'securityStore', 'futuresStore', 'homeStore', 'screenerStore']
    }))
    middleware.push(logger);
}


const commonTransform = createTransform(
    (inboundState: CommonStateType) => {
        return { ...inboundState }
    },
    (outboundState) => {
        return { ...outboundState }
    },
    { whitelist: ['commonStore'] }
)

const globalNotificationTransform = createTransform(
    (inboundState: GlobalNotificationsState) => ({
        dismissedNotifications: inboundState.dismissedNotifications
    }),
    (outboundState) => ({
        ...golbalNotificationInitialState,
        dismissedNotifications: outboundState.dismissedNotifications
    }),
    { whitelist: ['globalNotificationStore'] }
);

const SIMULATOR_MIN_TS = new Date('2025-01-01').getTime()

const simulatorTransform = createTransform(
    (inboundState: any) => {
        const validTimestamp = getLastWeekday()
        const ts = inboundState?.timestamp
        const isInvalid = !ts || ts < SIMULATOR_MIN_TS || ts > Date.now()
        return {
            ...inboundState,
            _lastSaved: new Date().toDateString(),
            ...(isInvalid && { timestamp: validTimestamp, initialTimestampSet: false })
        }
    },
    (outboundState: any) => {
        const today = new Date().toDateString()
        const lastSaved = outboundState._lastSaved
        if (!lastSaved || lastSaved !== today) {
            return {
                timestamp: getLastWeekday(),
                orders: [],
                SquareOffOrders: [],
                ordersPrices: [],
                orderSecurityKey: undefined,
                autoPlay: false,
                initialTimestampSet: false
            }
        }
        const { _lastSaved, ...stateWithoutTimestamp } = outboundState
        return stateWithoutTimestamp
    },
    { whitelist: ['simulatorStore'] }
);

const multiStrikeChartTransform = createTransform(
    (inboundState: any) => {
        return {
            ...inboundState,
            _lastSaved: new Date().toDateString()
        }
    },
    (outboundState: any) => {
        const today = new Date().toDateString()
        const lastSaved = outboundState._lastSaved
        if (!lastSaved || lastSaved !== today) {
            // Reset only OI settings if it's a new day, preserve everything else
            const { _lastSaved, ...stateWithoutTimestamp } = outboundState
            return {
                ...stateWithoutTimestamp,
                oiChangeSettings: homeInitialState.oiChangeSettings,
                openInterestSettings: homeInitialState.openInterestSettings
            }
        }
        const { _lastSaved, ...stateWithoutTimestamp } = outboundState
        return stateWithoutTimestamp
    },
    { whitelist: ['multiStrikeChartStore'] }
);

const migrateState = (state: PersistedState, version: number): Promise<any> => {
    return new Promise((resolve) => {
        if (state?._persist.version === version) {
            resolve(state)
        } else if (state?._persist.version !== version) {
            resolve({
                commonStore: commonInitialState,
                _persist: {
                    version,
                    rehydrated: true
                }
            })
        } else {
            resolve({ _persist: { version: -1, rehydrated: true } })
        }
    })
}

const getPersistConfig = () => {
    return {
        key: window.localStorage.getItem('storeKey') || 'anonymous',
        version: Redux_Store_Version,
        storage,
        whitelist: ['commonStore', 'multiStrikeChartStore', 'globalNotificationStore', 'companyEventsStore', 'recentSearchStore' , 'screenerChatStore', 'simulatorStore'],
        transforms: [commonTransform, globalNotificationTransform, simulatorTransform, multiStrikeChartTransform],
        migrate: (state: PersistedState, version: number): Promise<PersistedState> => migrateState(state, version)
    }
}

export const getCombinedReducers = () => {
    return combineReducers({
        commonStore: commonReducer,
        chartStore: chartReducer,
        userStore: userReducer,
        homeStore: homeReducer,
        screenerStore: screenerReducer,
        watchListStore: watchListReducer,
        overviewStore: overviewReducer,
        alertLogsStore: alertLogsReducer,
        alertsStore: alertsReducer,
        strategyBuilderStore: strategyBuilderReducer,
        websocketStore: websocketReducer,
        ordersStore: ordersReducer,
        securityStore: securityReducer,
        orderBookStore: orderBookReducer,
        optionsStore: optionsReducer,
        futuresStore: futuresReducer,
        widgetDashboardStore: widgetDashboardReducer,
        multiStrikeChartStore: multiStrikeChartReducer,
        positionsStore: positionsReducer,
        tourStore: tourReducer,
        strategyTemplateStore: strategyTemplateReducer,
        rehydrationStore: rehydrationReducer,
        globalNotificationStore: globalNotification,
        companyEventsStore: companyEventsReducer,
        simulatorStore: simulatorReducer,
        recentSearchStore: recentSearchReducer,
        screenerChatStore: screenerChatReducer,
        deviceStore: deviceReducer,
        quickTradeWidgetStore: quickTradeReducer,
        holdingsStore: holdingsReducer,
        scalperStore: scalperReducer,
        feedbackStore: feedbackReducer,
        heatmapStore: heatmapReducer,
    })
}

export const swapUserStore = (userId: string) => {
    window.localStorage.setItem('storeKey', userId)
    store.replaceReducer(getRootReducer())
    persistor && persistor.persist && persistor.persist()
}

const getRootReducer = isEmbedded ? getCombinedReducers : () => persistReducer(getPersistConfig(), getCombinedReducers())

const store = configureStore({
    devTools: enableDebug,
    reducer: getRootReducer(),
    middleware: (getDefaultMiddleware) => middleware
})

setupListeners(store.dispatch)

export const persistor = persistStore(store)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export default store